import { useEffect, useState, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { Toaster, toast } from "sonner";
import {
  Sparkles, Clock, MapPin, Phone, CheckCircle2, Star,
  ChevronRight, MessageCircle, Car, Droplets, ArrowRight,
  Menu, X, Camera, Loader2, Upload, Trash2
} from "lucide-react";
import { Button } from "./components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./components/ui/dialog";
import { Input } from "./components/ui/input";
import { Label } from "./components/ui/label";
import { Textarea } from "./components/ui/textarea";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "./components/ui/accordion";
import AdminPage from "./AdminPage";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const WHATSAPP_NUMBER = "61415315657";

const LOGO = "https://customer-assets.emergentagent.com/job_mobile-carwash-syd/artifacts/7uoji8y1_DF384A7A-7AC7-4436-8CE5-42688DD43CDD.PNG";

const VEHICLE_TYPES = ["Small / Hatchback", "Sedan", "SUV / 4x4", "Van / Ute", "Premium / Luxury"];
const SERVICE_OPTIONS = ["Essential Wash", "Complete Detail", "Not sure — recommend me"];

// WhatsApp Float Button
const WhatsAppButton = () => (
  <a
    href={`https://wa.me/${WHATSAPP_NUMBER}?text=Hi! I'd like a free quote for a mobile car wash in Sydney.`}
    target="_blank"
    rel="noopener noreferrer"
    data-testid="whatsapp-float-btn"
    className="fixed bottom-6 right-6 z-50 w-14 h-14 bg-[#25D366] rounded-full flex items-center justify-center shadow-lg hover:scale-110 transition-transform whatsapp-pulse"
  >
    <MessageCircle className="w-7 h-7 text-white fill-white" />
  </a>
);

// Navigation
const Navbar = ({ onQuoteClick }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <nav className="fixed top-0 left-0 right-0 z-40 bg-white/80 backdrop-blur-md border-b border-slate-200/50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <div className="flex items-center gap-2">
            <img src={LOGO} alt="SydneyShine Car Detailing" className="h-10 md:h-14 w-auto" />
          </div>

          <div className="hidden md:flex items-center gap-8">
            <a href="#services" className="text-slate-600 hover:text-slate-900 transition-colors">Services</a>
            <a href="#how-it-works" className="text-slate-600 hover:text-slate-900 transition-colors">How It Works</a>
            <a href="#testimonials" className="text-slate-600 hover:text-slate-900 transition-colors">Reviews</a>
            <a href="#faq" className="text-slate-600 hover:text-slate-900 transition-colors">FAQ</a>
            <Button onClick={onQuoteClick} data-testid="nav-quote-btn" className="bg-sky-500 hover:bg-sky-600 text-white">
              Get a Free Quote
            </Button>
          </div>

          <button className="md:hidden p-2" onClick={() => setIsOpen(!isOpen)} data-testid="mobile-menu-btn">
            {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {isOpen && (
          <div className="md:hidden py-4 border-t border-slate-200">
            <div className="flex flex-col gap-4">
              <a href="#services" className="text-slate-600 hover:text-slate-900">Services</a>
              <a href="#how-it-works" className="text-slate-600 hover:text-slate-900">How It Works</a>
              <a href="#testimonials" className="text-slate-600 hover:text-slate-900">Reviews</a>
              <a href="#faq" className="text-slate-600 hover:text-slate-900">FAQ</a>
              <Button onClick={onQuoteClick} className="bg-sky-500 hover:bg-sky-600 text-white w-full">
                Get a Free Quote
              </Button>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

// Hero Section
const HeroSection = ({ onQuoteClick }) => (
  <section className="relative min-h-screen flex items-center pt-16" data-testid="hero-section">
    <div
      className="absolute inset-0 bg-cover bg-center"
      style={{
        backgroundImage: `url('https://images.unsplash.com/photo-1590170817259-8a08e55a2610?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDJ8MHwxfHNlYXJjaHwxfHxsdXh1cnklMjBjYXIlMjBkZXRhaWxpbmclMjBmb2FtJTIwd2FzaGluZyUyMGNsb3NlJTIwdXB8ZW58MHx8fHwxNzcxMTM5NTY0fDA&ixlib=rb-4.1.0&q=85')`
      }}
    />
    <div className="absolute inset-0 hero-overlay" />

    <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
      <div className="grid lg:grid-cols-2 gap-12 items-center">
        <motion.div
          initial={{ opacity: 0, x: -50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8 }}
          className="text-white"
        >
          <span className="inline-block px-4 py-2 bg-sky-500/20 text-sky-300 rounded-full text-sm font-medium mb-6">
            <MapPin className="w-4 h-4 inline mr-2" />
            Servicing All Sydney
          </span>
          <h1 className="text-5xl md:text-7xl font-extrabold tracking-tight leading-tight mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Mobile Car<br />
            <span className="text-sky-400">Detailing</span><br />
            At Your Door
          </h1>
          <p className="text-xl text-slate-300 mb-8 max-w-lg">
            Professional car wash & detailing that comes to your home or office.
            Send us a few photos and get a tailored price for your vehicle.
          </p>
          <div className="flex flex-col sm:flex-row gap-4">
            <Button
              size="lg"
              onClick={onQuoteClick}
              data-testid="hero-quote-btn"
              className="bg-sky-500 hover:bg-sky-600 text-white text-lg px-8 py-6"
            >
              Get Your Free Quote
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
            <a href={`https://wa.me/${WHATSAPP_NUMBER}`} target="_blank" rel="noopener noreferrer">
              <Button size="lg" variant="outline" data-testid="hero-whatsapp-btn"
                className="border-white/30 text-white hover:bg-white/10 text-lg px-8 py-6 w-full sm:w-auto">
                <Phone className="mr-2 w-5 h-5" />
                0415 315 657
              </Button>
            </a>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, x: 50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="hidden lg:block"
        >
          <div className="glass-card rounded-2xl p-8 shadow-2xl">
            <div className="flex items-center gap-3 mb-6">
              <Sparkles className="w-6 h-6 text-amber-500" />
              <h3 className="text-xl font-semibold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Free, No-Obligation Quote
              </h3>
            </div>
            <div className="space-y-4">
              <div className="flex items-center gap-4 p-4 bg-slate-50 rounded-xl">
                <Camera className="w-8 h-8 text-sky-500 flex-shrink-0" />
                <p className="font-medium text-slate-900 text-sm">Send photos of your car</p>
              </div>
              <div className="flex items-center gap-4 p-4 bg-slate-50 rounded-xl">
                <MessageCircle className="w-8 h-8 text-sky-500 flex-shrink-0" />
                <p className="font-medium text-slate-900 text-sm">We review & send your custom price</p>
              </div>
              <div className="flex items-center gap-4 p-4 bg-amber-50 rounded-xl border-2 border-amber-200">
                <Car className="w-8 h-8 text-amber-500 flex-shrink-0" />
                <p className="font-medium text-slate-900 text-sm">We come to you & make it shine</p>
              </div>
              <Button className="w-full bg-slate-900 hover:bg-slate-800 text-white py-6" onClick={onQuoteClick} data-testid="quickquote-btn">
                Get My Quote
                <ChevronRight className="ml-2 w-5 h-5" />
              </Button>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  </section>
);

// Services Section (no prices)
const ServicesSection = ({ services, onQuoteClick }) => (
  <section id="services" className="py-20 md:py-32 bg-white" data-testid="services-section">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        className="text-center mb-16"
      >
        <span className="text-sky-500 font-medium text-sm tracking-wider uppercase mb-4 block">Our Services</span>
        <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
          Tailored To Your Vehicle
        </h2>
        <p className="text-lg text-slate-600 max-w-2xl mx-auto">
          Every car is different. Premium and luxury vehicles get extra care — that's why we price each job individually.
        </p>
      </motion.div>

      <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
        {services.map((service, index) => (
          <motion.div
            key={service.id}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: index * 0.1 }}
          >
            <Card
              className={`service-card relative overflow-hidden h-full ${service.popular ? 'border-2 border-amber-400 shadow-lg' : 'border border-slate-200'}`}
              data-testid={`service-card-${service.id}`}
            >
              {service.popular && (
                <div className="absolute top-0 right-0 shimmer-badge text-white text-xs font-bold px-4 py-2 rounded-bl-xl">
                  MOST POPULAR
                </div>
              )}
              <CardHeader className="pb-4">
                <div className="flex items-center gap-3 mb-2">
                  {service.popular ? <Sparkles className="w-8 h-8 text-amber-500" /> : <Droplets className="w-8 h-8 text-sky-500" />}
                  <div>
                    <CardTitle className="text-2xl" style={{ fontFamily: 'Manrope, sans-serif' }}>{service.name}</CardTitle>
                    <CardDescription className="flex items-center gap-2 mt-1">
                      <Clock className="w-4 h-4" />
                      {service.duration}
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-slate-600 mb-6">{service.description}</p>
                <ul className="space-y-3 mb-6">
                  {service.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start gap-3">
                      <CheckCircle2 className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                      <span className="text-slate-700">{feature}</span>
                    </li>
                  ))}
                </ul>
                <Button
                  className={`w-full py-6 ${service.popular ? 'bg-amber-500 hover:bg-amber-600' : 'bg-slate-900 hover:bg-slate-800'} text-white`}
                  onClick={() => onQuoteClick(service)}
                  data-testid={`quote-service-${service.id}-btn`}
                >
                  Get a Quote for {service.name}
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </CardContent>
            </Card>
          </motion.div>
        ))}
      </div>
    </div>
  </section>
);

// How It Works
const HowItWorksSection = () => {
  const steps = [
    { icon: Camera, title: "Send Your Details", description: "Tell us about your car and add a few photos for an accurate quote" },
    { icon: MessageCircle, title: "Get Your Price", description: "We review and send you a tailored price — no surprises" },
    { icon: Sparkles, title: "We Come To You", description: "Pick a time and we detail your car at your home or office" }
  ];

  return (
    <section id="how-it-works" className="py-20 md:py-32 bg-slate-50" data-testid="how-it-works-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <span className="text-sky-500 font-medium text-sm tracking-wider uppercase mb-4 block">Simple Process</span>
          <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>How It Works</h2>
        </motion.div>

        <div className="grid md:grid-cols-3 gap-8">
          {steps.map((step, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.15 }}
              className="relative"
            >
              <div className="text-center">
                <div className="w-20 h-20 bg-sky-500 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg shadow-sky-500/30">
                  <step.icon className="w-10 h-10 text-white" />
                </div>
                <span className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-2 w-8 h-8 bg-slate-900 text-white rounded-full flex items-center justify-center text-sm font-bold">
                  {index + 1}
                </span>
                <h3 className="text-xl font-semibold text-slate-900 mb-3" style={{ fontFamily: 'Manrope, sans-serif' }}>{step.title}</h3>
                <p className="text-slate-600">{step.description}</p>
              </div>
              {index < 2 && (
                <div className="hidden md:block absolute top-10 left-[60%] w-[80%] h-0.5 bg-gradient-to-r from-sky-500 to-transparent" />
              )}
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Testimonials
const TestimonialsSection = () => {
  const testimonials = [
    { name: "Sarah M.", suburb: "Bondi", rating: 5, text: "Absolutely fantastic service! My car hasn't looked this good since I bought it. The convenience of having them come to my apartment is a game changer." },
    { name: "James L.", suburb: "Parramatta", rating: 5, text: "Professional, punctual, and the results speak for themselves. I've been using SydneyShine for 6 months now - highly recommend!" },
    { name: "Michelle K.", suburb: "North Sydney", rating: 5, text: "The Complete Detail package is worth every dollar. They even got stains out of my seats that I thought were permanent. Amazing work!" }
  ];

  return (
    <section id="testimonials" className="py-20 md:py-32 bg-white" data-testid="testimonials-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <span className="text-sky-500 font-medium text-sm tracking-wider uppercase mb-4 block">Customer Reviews</span>
          <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>What Sydney Says</h2>
        </motion.div>

        <div className="grid md:grid-cols-3 gap-8">
          {testimonials.map((testimonial, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
            >
              <Card className="testimonial-card h-full border-slate-200">
                <CardContent className="pt-6">
                  <div className="flex gap-1 mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="w-5 h-5 text-amber-400 fill-amber-400" />
                    ))}
                  </div>
                  <p className="text-slate-600 mb-6 italic">"{testimonial.text}"</p>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-sky-100 rounded-full flex items-center justify-center">
                      <span className="text-sky-600 font-semibold">{testimonial.name[0]}</span>
                    </div>
                    <div>
                      <p className="font-medium text-slate-900">{testimonial.name}</p>
                      <p className="text-sm text-slate-500">{testimonial.suburb}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// FAQ
const FAQSection = () => {
  const faqs = [
    { question: "How much does it cost?", answer: "Because every vehicle is different, we give you a personalised price. Just send your details and a few photos and we'll get back to you with a tailored quote — completely free and with no obligation." },
    { question: "Why do premium cars cost more?", answer: "Luxury and premium vehicles need specialist products and extra care to protect their finish, so they require a bit more time and attention. That's why we quote each car individually." },
    { question: "Do I need to provide water or power?", answer: "No! We bring everything we need including our own water supply and equipment. All we need is access to your vehicle." },
    { question: "What areas do you service?", answer: "We service all of Greater Sydney including CBD, Eastern Suburbs, Inner West, North Shore, Northern Beaches, and Western Sydney." },
    { question: "What if it rains on my booking day?", answer: "We'll contact you to reschedule at no extra cost. Your satisfaction is our priority!" }
  ];

  return (
    <section id="faq" className="py-20 md:py-32 bg-slate-50" data-testid="faq-section">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <span className="text-sky-500 font-medium text-sm tracking-wider uppercase mb-4 block">FAQ</span>
          <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>Common Questions</h2>
        </motion.div>

        <Accordion type="single" collapsible className="space-y-4">
          {faqs.map((faq, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.05 }}
            >
              <AccordionItem value={`item-${index}`} className="bg-white rounded-xl border border-slate-200 px-6" data-testid={`faq-item-${index}`}>
                <AccordionTrigger className="text-left font-medium text-slate-900 hover:no-underline">{faq.question}</AccordionTrigger>
                <AccordionContent className="text-slate-600">{faq.answer}</AccordionContent>
              </AccordionItem>
            </motion.div>
          ))}
        </Accordion>
      </div>
    </section>
  );
};

// Footer
const Footer = () => (
  <footer className="bg-slate-900 text-white py-16" data-testid="footer">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="grid md:grid-cols-4 gap-8">
        <div className="md:col-span-2">
          <div className="flex items-center gap-2 mb-4">
            <img src={LOGO} alt="SydneyShine Car Detailing" className="h-12 w-auto" />
          </div>
          <p className="text-slate-400 mb-4 max-w-md">
            Sydney's premium mobile car detailing service. We bring the shine to your doorstep.
          </p>
          <div className="flex items-center gap-2 text-slate-400">
            <Phone className="w-5 h-5" />
            <a href="tel:0415315657" className="hover:text-white transition-colors">0415 315 657</a>
          </div>
        </div>
        <div>
          <h4 className="font-semibold mb-4">Services</h4>
          <ul className="space-y-2 text-slate-400">
            <li>Essential Wash</li>
            <li>Complete Detail</li>
            <li>Custom quote on request</li>
          </ul>
        </div>
        <div>
          <h4 className="font-semibold mb-4">Service Areas</h4>
          <ul className="space-y-2 text-slate-400">
            <li>Sydney CBD</li>
            <li>Eastern Suburbs</li>
            <li>North Shore</li>
            <li>Inner West</li>
          </ul>
        </div>
      </div>
      <div className="border-t border-slate-800 mt-12 pt-8 text-center text-slate-500 text-sm">
        © {new Date().getFullYear()} SydneyShine Mobile Car Detailing. All rights reserved.
      </div>
    </div>
  </footer>
);

// Quote Dialog
const QuoteDialog = ({ isOpen, onClose, selectedService, services }) => {
  const fileInputRef = useRef(null);
  const [formData, setFormData] = useState({
    name: "", phone: "", email: "", address: "", suburb: "",
    vehicle: "", vehicle_type: "", service_interest: "", notes: ""
  });
  const [photos, setPhotos] = useState([]); // { id, url, name, uploading }
  const [isUploading, setIsUploading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (selectedService) {
      setFormData(prev => ({ ...prev, service_interest: selectedService.name }));
    }
  }, [selectedService]);

  const handleFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setIsUploading(true);
    for (const file of files) {
      try {
        const fd = new FormData();
        fd.append("file", file);
        const res = await axios.post(`${API}/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        setPhotos(prev => [...prev, { id: res.data.id, url: `${BACKEND_URL}${res.data.url}`, name: file.name }]);
      } catch (err) {
        console.error("Upload error:", err);
        toast.error(`Could not upload ${file.name}`);
      }
    }
    setIsUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const removePhoto = (id) => setPhotos(prev => prev.filter(p => p.id !== id));

  const isValid = formData.name && formData.phone && formData.vehicle && formData.vehicle_type;

  const handleSubmit = async () => {
    if (!isValid) return;
    setIsSubmitting(true);
    try {
      await axios.post(`${API}/quotes`, { ...formData, photo_ids: photos.map(p => p.id) });

      const msg =
        `Hi SydneyShine! I'd like a free quote.%0A%0A` +
        `Name: ${formData.name}%0A` +
        `Phone: ${formData.phone}%0A` +
        (formData.email ? `Email: ${formData.email}%0A` : "") +
        `Vehicle: ${formData.vehicle} (${formData.vehicle_type})%0A` +
        (formData.service_interest ? `Service: ${formData.service_interest}%0A` : "") +
        (formData.suburb ? `Suburb: ${formData.suburb}%0A` : "") +
        (formData.notes ? `Notes: ${formData.notes}%0A` : "") +
        (photos.length ? `I've also uploaded ${photos.length} photo(s) of my car.` : "");

      toast.success("Quote request sent! Redirecting you to WhatsApp...");
      setTimeout(() => {
        window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${msg}`, "_blank");
        onClose();
        setFormData({ name: "", phone: "", email: "", address: "", suburb: "", vehicle: "", vehicle_type: "", service_interest: "", notes: "" });
        setPhotos([]);
      }, 800);
    } catch (err) {
      console.error("Quote error:", err);
      toast.error("Something went wrong. Please try again or call us.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="quote-dialog">
        <DialogHeader>
          <DialogTitle className="text-2xl" style={{ fontFamily: 'Manrope, sans-serif' }}>Get Your Free Quote</DialogTitle>
          <DialogDescription>
            Tell us about your vehicle. Add a few photos for the most accurate price. It's free and there's no obligation.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="name">Full Name *</Label>
              <Input id="name" value={formData.name} onChange={(e) => setFormData(p => ({ ...p, name: e.target.value }))} placeholder="John Smith" data-testid="quote-name-input" />
            </div>
            <div>
              <Label htmlFor="phone">Phone *</Label>
              <Input id="phone" value={formData.phone} onChange={(e) => setFormData(p => ({ ...p, phone: e.target.value }))} placeholder="0412 345 678" data-testid="quote-phone-input" />
            </div>
            <div>
              <Label htmlFor="email">Email (optional)</Label>
              <Input id="email" type="email" value={formData.email} onChange={(e) => setFormData(p => ({ ...p, email: e.target.value }))} placeholder="john@example.com" data-testid="quote-email-input" />
            </div>
            <div>
              <Label htmlFor="suburb">Suburb (optional)</Label>
              <Input id="suburb" value={formData.suburb} onChange={(e) => setFormData(p => ({ ...p, suburb: e.target.value }))} placeholder="Bondi" data-testid="quote-suburb-input" />
            </div>
            <div>
              <Label htmlFor="vehicle">Car Make & Model *</Label>
              <Input id="vehicle" value={formData.vehicle} onChange={(e) => setFormData(p => ({ ...p, vehicle: e.target.value }))} placeholder="e.g. BMW X5, Toyota Corolla" data-testid="quote-vehicle-input" />
            </div>
            <div>
              <Label htmlFor="vehicle_type">Vehicle Type *</Label>
              <select
                id="vehicle_type"
                value={formData.vehicle_type}
                onChange={(e) => setFormData(p => ({ ...p, vehicle_type: e.target.value }))}
                data-testid="quote-vehicle-type-select"
                className="flex h-10 w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500"
              >
                <option value="">Select...</option>
                {VEHICLE_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
          </div>

          <div>
            <Label htmlFor="service_interest">Service You're Interested In</Label>
            <select
              id="service_interest"
              value={formData.service_interest}
              onChange={(e) => setFormData(p => ({ ...p, service_interest: e.target.value }))}
              data-testid="quote-service-select"
              className="flex h-10 w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500"
            >
              <option value="">Select...</option>
              {SERVICE_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>

          <div>
            <Label htmlFor="address">Street Address (optional)</Label>
            <Input id="address" value={formData.address} onChange={(e) => setFormData(p => ({ ...p, address: e.target.value }))} placeholder="123 Beach Road" data-testid="quote-address-input" />
          </div>

          <div>
            <Label htmlFor="notes">Additional Notes (optional)</Label>
            <Textarea id="notes" value={formData.notes} onChange={(e) => setFormData(p => ({ ...p, notes: e.target.value }))} placeholder="e.g. heavy interior mess, pet hair, bird droppings..." data-testid="quote-notes-input" />
          </div>

          {/* Photo upload */}
          <div>
            <Label>Vehicle Photos (optional, recommended)</Label>
            <input ref={fileInputRef} type="file" accept="image/*" multiple onChange={handleFiles} className="hidden" data-testid="quote-photo-input" />
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              data-testid="quote-photo-upload-btn"
              className="mt-1 w-full border-2 border-dashed border-slate-300 rounded-xl p-6 flex flex-col items-center justify-center text-slate-500 hover:border-sky-400 hover:text-sky-500 transition-colors"
            >
              {isUploading ? <Loader2 className="w-6 h-6 animate-spin mb-2" /> : <Upload className="w-6 h-6 mb-2" />}
              <span className="text-sm font-medium">{isUploading ? "Uploading..." : "Tap to add photos of your car"}</span>
            </button>
            {photos.length > 0 && (
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-3 mt-3" data-testid="quote-photo-list">
                {photos.map(p => (
                  <div key={p.id} className="relative group">
                    <img src={p.url} alt={p.name} className="w-full h-20 object-cover rounded-lg border border-slate-200" />
                    <button
                      type="button"
                      onClick={() => removePhoto(p.id)}
                      className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center shadow"
                      data-testid={`remove-photo-${p.id}`}
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <Button
            className="w-full bg-sky-500 hover:bg-sky-600 py-6 text-lg"
            disabled={!isValid || isSubmitting || isUploading}
            onClick={handleSubmit}
            data-testid="quote-submit-btn"
          >
            {isSubmitting ? <><Loader2 className="mr-2 w-5 h-5 animate-spin" /> Sending...</> : <>Send My Free Quote Request <MessageCircle className="ml-2 w-5 h-5" /></>}
          </Button>
          <p className="text-xs text-center text-slate-500">
            After sending, you'll be taken to WhatsApp so we can confirm your details quickly.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Home Page
const HomePage = () => {
  const [services, setServices] = useState([]);
  const [isQuoteOpen, setIsQuoteOpen] = useState(false);
  const [selectedService, setSelectedService] = useState(null);

  useEffect(() => {
    axios.get(`${API}/services`)
      .then(res => setServices(res.data))
      .catch(() => setServices([
        { id: "essential", name: "Essential Wash", description: "Exterior hand wash & refresh to bring back the shine.", features: ["Hand wash exterior", "Wheel & tire cleaning", "Windows cleaned inside & out", "Tire shine applied"], duration: "60-90 mins" },
        { id: "complete", name: "Complete Detail", description: "Full interior & exterior detailing with a premium finish.", features: ["Everything in Essential Wash", "Full interior vacuum", "Dashboard & console cleaning", "Leather/fabric conditioning"], duration: "120-180 mins", popular: true }
      ]));
  }, []);

  const openQuote = (service = null) => {
    setSelectedService(service);
    setIsQuoteOpen(true);
  };

  return (
    <div className="min-h-screen" data-testid="home-page">
      <Navbar onQuoteClick={() => openQuote(null)} />
      <HeroSection onQuoteClick={() => openQuote(null)} />
      <ServicesSection services={services} onQuoteClick={openQuote} />
      <HowItWorksSection />
      <TestimonialsSection />
      <FAQSection />
      <Footer />
      <WhatsAppButton />
      <QuoteDialog isOpen={isQuoteOpen} onClose={() => setIsQuoteOpen(false)} selectedService={selectedService} services={services} />
    </div>
  );
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <Toaster position="top-center" richColors />
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/admin" element={<AdminPage />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
